import React from "react";
import { cloudImg } from "../services/api";
import { formatPrice } from "../utils/currency";

// The cart drawer ("Your order"): line items with modifier checkboxes
// and per-item note inputs, the "often ordered with" suggestion strip,
// order-level notes textarea, total row, and the submit button
// (including the submitting / reconnecting label logic).
export default function CartPanel({
  cartItems,
  total,
  tableInfo,
  oftenWith,
  notes,
  setNotes,
  error,
  submitting,
  retrying,
  submit,
  onClose,
  addOne,
  removeOne,
  toggleModifier,
  setItemNote,
  addItemToCart,
  pickName,
  lang,
  t,
  currency,
}) {
  const L = (en, am, om) => (lang === "am" ? am : lang === "om" ? om : en);
  const svcPct = Number(tableInfo?.service_charge_pct || 0);
  const vatPct = Number(tableInfo?.vat_pct || 0);
  const inclVat = !!tableInfo?.prices_include_vat;
  const r2 = (v) => Math.round(v * 100) / 100;
  const service = r2((total * svcPct) / 100);
  const base = r2(total + service);
  const vat = r2(inclVat ? (vatPct ? base - base / (1 + vatPct / 100) : 0) : (base * vatPct) / 100);
  const grand = r2(inclVat ? base : base + vat);
  const hasCharges = service > 0 || vat > 0;
  return (
    <div className="cart-drawer" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <h2>{t("your_order")} &middot; {t("table")} {tableInfo?.table_number}</h2>
        {cartItems.map(({ item, quantity, note, modifiers: selMods }) => {
          const sel = selMods || [];
          const surcharge = sel.reduce((s, m) => s + (m.price || 0), 0);
          const lineTotal = (item.price + surcharge) * quantity;
          return (
            <div key={item.id} className="cart-line-block">
              <div className="cart-line">
                <span className="name" lang={lang}>
                  {pickName(item)} <span className="muted">&times; {quantity}</span>
                </span>
                <span>{formatPrice(lineTotal, currency)}</span>
                <div className="qty" style={{ marginLeft: 12 }}>
                  <button onClick={() => removeOne(item)}>&minus;</button>
                  <span>{quantity}</span>
                  <button onClick={() => addOne(item)}>+</button>
                </div>
              </div>
              {item.modifiers && item.modifiers.length > 0 && (
                <div className="line-mods">
                  {item.modifiers.map((m) => {
                    const checked = !!sel.find((x) => x.name === m.name);
                    return (
                      <label key={m.name} className="mod-chip">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleModifier(item, m)}
                        />
                        <span>{m.name}</span>
                        {m.price > 0 && <span className="muted">+{formatPrice(m.price, currency)}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              <input
                className="line-note"
                type="text"
                placeholder="Note for this item (e.g. no onion, extra spicy)"
                value={note || ""}
                onChange={(e) => setItemNote(item, e.target.value)}
              />
            </div>
          );
        })}
        {/* OFTEN ORDERED WITH — co-occurrence suggestions for the
            current cart. Hidden when there's no history or no
            available co-ordered items. Tap a card to add 1 to cart;
            the strip re-fetches and may replace the suggestion with
            another. Compact horizontal scroll on phones. */}
        {oftenWith.length > 0 && (
          <div
            style={{
              margin: "8px 0 14px",
              padding: "10px 12px",
              background: "#fbf6e6",
              border: "1px solid #ecd98e",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                fontSize: ".82rem",
                fontWeight: 700,
                color: "#7e671f",
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>✨</span>
              {lang === "nl" ? "Vaak besteld bij deze gerechten" :
               lang === "am" ? "ከእነዚህ ጋር ብዙ ጊዜ የተዘዙ" :
               lang === "om" ? "Kanaan walitti yeroo baay'ee ajajaman" :
               lang === "sid" ? "Often ordered with these" :
               "Often ordered with these"}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                paddingBottom: 4,
              }}
            >
              {oftenWith.map((item) => {
                const img = cloudImg(item.image_url, 120);
                return (
                  <button
                    key={`often-${item.id}`}
                    type="button"
                    className="tap-feedback"
                    onClick={() => addItemToCart(item)}
                    style={{
                      flex: "0 0 110px",
                      background: "#fff",
                      border: "1px solid #ecd98e",
                      borderRadius: 10,
                      padding: 6,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    aria-label={`Add ${pickName(item)} to order`}
                  >
                    <div
                      style={{
                        width: "100%", height: 64,
                        borderRadius: 6, overflow: "hidden",
                        background: "#f0ece6", marginBottom: 4,
                      }}
                    >
                      {img ? (
                        <img
                          src={img} alt={pickName(item)} loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%", height: "100%",
                            display: "flex", alignItems: "center",
                            justifyContent: "center",
                            color: "#b9b2a4", fontWeight: 700,
                          }}
                        >
                          {pickName(item).slice(0, 1)}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontWeight: 600, fontSize: ".78rem",
                        lineHeight: 1.2, whiteSpace: "nowrap",
                        overflow: "hidden", textOverflow: "ellipsis",
                      }}
                      lang={lang}
                    >
                      {pickName(item)}
                    </div>
                    <div
                      style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginTop: 3,
                      }}
                    >
                      <span style={{ fontSize: ".72rem", fontWeight: 700 }}>
                        {formatPrice(item.price, currency)}
                      </span>
                      <span
                        style={{
                          background: "#d4af37",
                          color: "#fff",
                          padding: "1px 6px",
                          borderRadius: 999,
                          fontSize: ".68rem",
                          fontWeight: 700,
                        }}
                      >
                        + {t("add")}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <textarea
          className="notes"
          placeholder={t("notes_placeholder")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {hasCharges ? (
          <div style={{ margin: "8px 0", fontSize: ".9rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
              <span>{L("Subtotal", "ንዑስ ድምር", "Ida'ama xiqqaa")}</span><span>{formatPrice(total, currency)}</span>
            </div>
            {service > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span>{L("Service charge", "የአገልግሎት ክፍያ", "Kaffaltii tajaajilaa")} ({svcPct}%)</span><span>{formatPrice(service, currency)}</span>
              </div>
            )}
            {vat > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span>{L("VAT", "ቫት", "VAT")} ({vatPct}%{inclVat ? " incl." : ""})</span><span>{formatPrice(vat, currency)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e3e8ea", paddingTop: 4 }}>
              <span>{t("total")}</span><span>{formatPrice(grand, currency)}</span>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, margin: "8px 0" }}>
            <span>{t("total")}</span>
            <span>{formatPrice(total, currency)}</span>
          </div>
        )}

        {/* How to pay — manual Telebirr / CBE Birr transfer (Phase 1).
            Shown only if the venue has set its payment details. */}
        {(tableInfo?.telebirr_number || tableInfo?.cbe_birr_number || tableInfo?.payment_qr_url) && (
          <div style={{ margin: "10px 0", padding: 12, background: "#eef7f7", border: "1px solid #bfe3e3", borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: ".9rem", marginBottom: 6, color: "#063b48" }}>
              💳 {lang === "am" ? "እንዴት እንደሚከፍሉ" : lang === "om" ? "Akkaataa kaffaltii" : "How to pay"}
            </div>
            {/* Scan-to-pay: guest scans the venue QR in their own Telebirr/CBE
                app; the account number itself is never shown to the guest. */}
            {tableInfo?.payment_qr_url ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: ".8rem", color: "#33505a", marginBottom: 8 }}>
                  {lang === "am"
                    ? "የቴሌብር/ሲቢኢ መተግበሪያዎን ይክፈቱ፣ ይህን ኮድ ይቃኙ እና ይክፈሉ።"
                    : lang === "om"
                    ? "Telebirr/CBE keessan banaa, koodii kana skaanii godhaatii kaffalaa."
                    : "Open your Telebirr / CBE app, scan this code, and pay."}
                </div>
                <img src={tableInfo.payment_qr_url} alt="Payment QR"
                  style={{ width: 200, maxWidth: "70%", borderRadius: 8, border: "1px solid #cfe6e6" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div style={{ fontWeight: 800, fontSize: "1.1rem", marginTop: 8, color: "#063b48" }}>
                  {formatPrice(grand, currency)}
                </div>
                <div style={{ fontSize: ".74rem", color: "#5f6b78", marginTop: 4 }}>
                  {lang === "am"
                    ? "ከከፈሉ በኋላ ማረጋገጫዎን ለአስተናጋጁ ያሳዩ።"
                    : lang === "om"
                    ? "Erga kaffaltanii booda ragaa keessan tajaajilaadhaaf agarsiisaa."
                    : "After paying, show your confirmation to the waiter."}
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: ".8rem", color: "#33505a", marginBottom: 8 }}>
                  {lang === "am"
                    ? "ጠቅላላውን ከታች ወዳለው ቁጥር ያስተላልፉ፣ ከዚያ ማረጋገጫዎን ለአስተናጋጁ ያሳዩ።"
                    : lang === "om"
                    ? "Ida'ama gara lakkoofsa gadiitti dabarsi, ergasii ragaa kee tajaajilaadhaaf agarsiisi."
                    : "Transfer the total to the number below, then show your confirmation to the waiter."}
                </div>
                {tableInfo?.telebirr_number && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".88rem", padding: "3px 0" }}>
                    <span>Telebirr</span><b>{tableInfo.telebirr_number}</b>
                  </div>
                )}
                {tableInfo?.cbe_birr_number && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".88rem", padding: "3px 0" }}>
                    <span>CBE Birr</span><b>{tableInfo.cbe_birr_number}</b>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {error && <div className="notice">{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn secondary" onClick={onClose}>
            {lang === "nl"
              ? "Verder kijken"
              : lang === "am"
              ? "ቀጥሎ ይመልከቱ"
              : lang === "om"
              ? "Itti fufii ilaali"
              : "Keep browsing"}
          </button>
          <button className="btn" disabled={submitting} onClick={submit}>
            {submitting
              ? (retrying ? t("reconnecting") : t("submitting"))
              : t("submit_order")}
          </button>
        </div>
      </div>
    </div>
  );
}
